package fr.frux.shared.content

import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

/**
 * Bornes d'une correction d'identité d'arbre (spec 2026-10-19). Partagées par l'API, qui refuse,
 * et par les clients, qui dessinent le cercle de déplacement et comptent le motif : la limite ne
 * peut pas dériver entre les deux, même doctrine que [DescriptionLength].
 */
object TreeCorrectionLimits {
    /** Au-delà, ce n'est plus une correction mais sans doute un autre arbre. */
    const val MAX_MOVE_METERS = 50.0

    /**
     * En deçà, un déplacement est du bruit de carte et non une correction : ni écriture ni
     * révision. Les builds publiés renvoient la position de leur mini-carte à chaque édition.
     */
    const val POSITION_NOISE_METERS = 1.0

    /** Motif facultatif d'une correction, même borne que la contrainte SQL de V71. */
    const val REASON_MAX = 280

    private const val EARTH_RADIUS_METERS = 6_371_008.8

    /** Mesuré en unités UTF-16 comme [DescriptionLength] : plus strict que `char_length`, jamais l'inverse. */
    fun isReasonTooLong(reason: String?): Boolean = (reason?.length ?: 0) > REASON_MAX

    /** Distance orthodromique (haversine) en mètres, même rayon moyen que `core-maps`. */
    fun distanceMeters(
        fromLat: Double,
        fromLon: Double,
        toLat: Double,
        toLon: Double,
    ): Double {
        val dLat = (toLat - fromLat).toRadians()
        val dLon = (toLon - fromLon).toRadians()
        val a =
            sin(dLat / 2) * sin(dLat / 2) +
                cos(fromLat.toRadians()) * cos(toLat.toRadians()) * sin(dLon / 2) * sin(dLon / 2)
        return 2 * EARTH_RADIUS_METERS * atan2(sqrt(a), sqrt(1 - a))
    }

    private fun Double.toRadians(): Double = this * PI / 180.0
}
